import {ResourcePageRequestOptions} from '../../core/services/resource-page-state.service';
import { Injectable } from '@angular/core';
import { PostRestService } from '../../api/services/post-rest.service';
import { PostApiModel } from '../../api/models/post-api.model';
import { PostViewModel } from './post.view-model';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { UserViewModel } from '../../features/user/settings/user.view-model';
import { ResultListApiModel } from '../../api/models/result-list.api.model';
import { MediaObjectViewModel } from '../../core/models/media-object.view-model';
import { QueryParamsApiModel } from '../../api/models/query-params-api.model';
import { FilterParameterApiModel } from '../../api/models/filter-parameter.api.model';
import { MediaObjectApiModel } from '../../api/models/media-object.api-model';
import { MediaObjectRestService } from '../../api/services/media-object.rest.service';
import { CommentRestService } from '../../api/services/comment.rest.service';
import { plainToClass } from 'class-transformer';
import { CommentApiModel } from '../../api/models/comment.api.model';
import { ProjectViewModel } from '../../features/user/garage/project.view-model';
import { ProjectApiModel } from '../../api/models/project.api-model';
import { EventApiModel } from '../../api/models/event.api.model';
import { EventRestService } from '../../api/services/event.rest.service';
import { ProjectRestService } from '../../api/services/project.rest.service';
import { VehicleViewModel } from '../../features/user/garage/vehicle.view-model';
import { VehicleApiModel } from '../../api/models/vehicle.api-model';
import { VehicleRestService } from '../../api/services/vehicle.rest.service';
import { ReactionApiModel } from '../../api/models/reaction.api.model';
import { ReactionRestService } from '../../api/services/reaction.rest.service';
import { ReactionTypeEnum } from "../../core/enums/reaction-type.enum";
import { ReactionSubjectInterface } from "./timeline-tile/reaction-subject.interface";
import { UserRestService } from "../../api/services/user.rest.service";
import { UserApiModel } from "../../api/models/user.api-model";
import { PostTypeEnum } from "../../core/enums/post-type.enum";
import { MediaTypeEnum } from '../../core/enums/media-type.enum';
import { MediaObjectInterface } from '../../core/interfaces/media-object.interface';

export interface TimelinePageResult {
  records: PostViewModel[];
  total: number;
  page: number;
}

@Injectable({
  providedIn: 'root'
})
export class TimelineDataProviderService {
  public items: PostViewModel[];
  public page: number;
  public endOfResults: boolean;
  constructor(
    private postRS: PostRestService,
    private mediaObjectRS: MediaObjectRestService,
    private eventRS: EventRestService,
    private projectRS: ProjectRestService,
    private vehicleRS: VehicleRestService,
    private userRS: UserRestService,
    private commentRS: CommentRestService,
    private reactionRS: ReactionRestService,
  ) { }

  public createPost(postVM: PostViewModel): Observable<PostViewModel> {
    const postInput = postVM.toApiModel();
    return this.postRS.create(postInput).pipe(map((postAM: PostApiModel) => {
      const newPostVM = new PostViewModel(postAM);
      newPostVM.project = postVM.project;
      newPostVM.event = postVM.event || newPostVM.event;
      newPostVM.shareRef = postAM.shareRef ?? postVM.shareRef ?? null;
      newPostVM.shareCard = postAM.shareCard ?? postVM.shareCard ?? null;
      return newPostVM;
    }));
  }

  public updatePost(postVM: PostViewModel): Observable<PostViewModel> {
    const postAM = postVM.apiModel;
    postAM.content = postVM.content;
    postAM.media = postVM.media.map(m => m['@id']);
    return this.postRS.put(postVM.apiModel).pipe(map((updatedPostAM: PostApiModel) => {
      const targetPostVM = postVM as PostViewModel & { fromApiModel?: (apiModel: PostApiModel) => PostViewModel };
      if (!(targetPostVM instanceof PostViewModel)) {
        Object.setPrototypeOf(targetPostVM, PostViewModel.prototype);
      }
      targetPostVM.apiModel = updatedPostAM;
      targetPostVM.fromApiModel(updatedPostAM);
      targetPostVM.postedBy = postVM.postedBy || targetPostVM.postedBy;
      targetPostVM.project = postVM.project || targetPostVM.project;
      targetPostVM.event = postVM.event || targetPostVM.event;
      targetPostVM.collaboration = postVM.collaboration || targetPostVM.collaboration;
      targetPostVM.localThumbnailUri = postVM.localThumbnailUri || null;
      return targetPostVM;
    }));
  }

  public getPost(id: string, withSoftDeleted: boolean, options?: ResourcePageRequestOptions): Observable<PostViewModel> {
    const params = new QueryParamsApiModel();
    if (withSoftDeleted) {
      params.filters.push(new FilterParameterApiModel('soft_deleted', 1, 'single'));
    }
    return this.postRS.get(id, params, options).pipe(mergeMap((postAM: PostApiModel) => {
      const deps = forkJoin([
        this.requiresProjectHydration(postAM) ? this.getProjectsCollection([postAM]).pipe(catchError(() => of([]))) : of([]),
        this.requiresEventHydration(postAM) ? this.getEventCollection([postAM]).pipe(catchError(() => of([]))) : of([])
      ]);
      return deps.pipe(map((
        [projectVMs, eventAMs]: [ProjectViewModel[], EventApiModel[]]) => {
        const postVM = new PostViewModel(postAM);
        const userVM = new UserViewModel(postAM.postedBy);
        userVM.avatar = postAM.postedBy.avatar ? new MediaObjectViewModel(postAM.postedBy.avatar as MediaObjectApiModel) : null;
        postVM.postedBy = userVM;
        if (postAM.project) {
          const projectIri = typeof postAM.project === 'string' ? postAM.project : postAM.project['@id'];
          postVM.project = this.hasEmbeddedProject(postAM.project)
            ? new ProjectViewModel(postAM.project)
            : projectVMs.find((projectVM: ProjectViewModel) => projectVM['@id'] === projectIri);
        }
        if (postAM.event) {
          const eventIri = typeof postAM.event === 'string' ? postAM.event : postAM.event['@id'];
          postVM.event = this.hasEmbeddedEvent(postAM.event)
            ? postAM.event
            : eventAMs.find((eventAM: EventApiModel) => eventAM['@id'] === eventIri);
        }
        return postVM;
      }));
    }));
  }

  public featuredPostUpdate$: Subject<PostViewModel> = new Subject<PostViewModel>();

  public togglePostFeatured(postVM: PostViewModel): Observable<PostViewModel> {
    if (!(postVM instanceof PostViewModel)) {
      postVM = new PostViewModel(postVM['apiModel'] ? postVM['apiModel'] : postVM);
    }
    const postAM = postVM.toApiModel();
    const nextFeaturedAt = postVM.featuredAt ? null : new Date();
    postAM.featuredAt = nextFeaturedAt;
    return this.postRS.markAsFeatured(postAM).pipe(map((postAM: PostApiModel) => {
      postVM.featuredAt = postAM.featuredAt;
      if (postVM.apiModel) {
        postVM.apiModel.featuredAt = postAM.featuredAt;
      }
      this.featuredPostUpdate$.next(postVM);
      return postVM;
    }));
  }

  public removePost(postVM: PostViewModel): Observable<any> {
    const postAM = postVM.apiModel;
    return this.postRS.delete(postAM);
  }

  public approvePost(postVM: PostViewModel, reviewNote?: string): Observable<PostViewModel> {
    return this.postRS.approve(postVM.toApiModel(), reviewNote).pipe(map((postAM: PostApiModel) => new PostViewModel(postAM)));
  }

  public rejectPost(postVM: PostViewModel, reviewNote?: string): Observable<PostViewModel> {
    return this.postRS.reject(postVM.toApiModel(), reviewNote).pipe(map((postAM: PostApiModel) => new PostViewModel(postAM)));
  }

  public getPostCollection(
    project: ProjectViewModel,
    event: EventApiModel,
    page: number,
    postTypes: string[],
    collaborationId?: number | string | null,
    mediaType?: MediaTypeEnum | null,
  ): Observable<TimelinePageResult> {
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('limit', collaborationId ? 100 : 9, 'single'));
    queryParams.page = page;

    postTypes.forEach((postType: string) => {
      queryParams.filters.push(new FilterParameterApiModel('discriminator', postType, 'array'));

    });

    if (mediaType) {
      queryParams.filters.push(new FilterParameterApiModel('mediaType', mediaType, 'single'));
    }

    const projectId = this.resolveProjectId(project);
    if (projectId !== null) {
      queryParams.filters.push(new FilterParameterApiModel('project', projectId, 'single'));
    }

    if (event) {
      queryParams.filters.push(new FilterParameterApiModel('event', event.id, 'single'));

    }

    if (collaborationId !== null && collaborationId !== undefined) {
      queryParams.filters.push(new FilterParameterApiModel('collaboration', collaborationId, 'single'));
    }

    try {
      return this.postRS.getCollection(queryParams).pipe(mergeMap((resultListAM: ResultListApiModel<PostApiModel>) => {
        const requiresProjectHydration = !project && resultListAM.records.some((postAM: PostApiModel) => this.requiresProjectHydration(postAM));
        const requiresEventHydration = !event && resultListAM.records.some((postAM: PostApiModel) => this.requiresEventHydration(postAM));
        const deps = forkJoin([
          project ? of([project]) : (requiresProjectHydration ? this.getProjectsCollection(resultListAM.records) : of([])),
          event ? of([event]) : (requiresEventHydration ? this.getEventCollection(resultListAM.records) : of([]))
        ]);
        return deps.pipe(map((
          [projectVMs, eventAMs]: [ProjectViewModel[], EventApiModel[]]) => {
          const records = resultListAM.records.map((postAM: PostApiModel) => {
            const postVM = new PostViewModel(postAM);
            const userVM = new UserViewModel(postAM.postedBy);
            userVM.avatar = postAM.postedBy.avatar ? new MediaObjectViewModel(postAM.postedBy.avatar as MediaObjectApiModel) : null;
            postVM.postedBy = userVM;
            if (postAM.project) {
              if (project) {
                postVM.project = project;
              } else if (this.hasEmbeddedProject(postAM.project)) {
                postVM.project = new ProjectViewModel(postAM.project as ProjectApiModel);
              } else {
                postVM.project = projectVMs.find((projectVM: ProjectViewModel) => projectVM['@id'] === (postAM.project as any)['@id']);
              }
            }
            if (postAM.event) {
              if (event) {
                postVM.event = event;
              } else if (this.hasEmbeddedEvent(postAM.event)) {
                postVM.event = postAM.event as EventApiModel;
              } else {
                postVM.event = eventAMs.find((eventAM: EventApiModel) => eventAM['@id'] === (postAM.event as any)['@id']);
              }
            }
            return postVM;
          });
          return {
            records,
            total: resultListAM.total,
            page: queryParams.page || 1,
          };
        }));
      }));
    } catch (e) {
      throw e;
    }
  }

  private resolveProjectId(project: ProjectViewModel | null | undefined): number | string | null {
    const id = project?.id as any;
    if (id === null || id === undefined) {
      return null;
    }
    const normalized = String(id).trim();
    if (!normalized || normalized === 'undefined' || normalized === 'null') {
      return null;
    }
    return id;
  }

  private requiresProjectHydration(postAM: PostApiModel): boolean {
    return !!postAM.project && !this.hasEmbeddedProject(postAM.project);
  }

  private requiresEventHydration(postAM: PostApiModel): boolean {
    return !!postAM.event && !this.hasEmbeddedEvent(postAM.event);
  }

  private hasEmbeddedProject(project: string | ProjectApiModel | null | undefined): project is ProjectApiModel {
    return !!project && typeof project !== 'string' && !!(project as ProjectApiModel).slug;
  }

  private hasEmbeddedEvent(event: string | EventApiModel | null | undefined): event is EventApiModel {
    return !!event && typeof event !== 'string' && !!(event as EventApiModel).slug;
  }

  public getMediaObjectCollection(postAMs: PostApiModel[]): Observable<MediaObjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = postAMs
      .filter((e: PostApiModel) => typeof e.postedBy.avatar === 'string')
      .map(e => new FilterParameterApiModel('id', e.postedBy.avatar));
    if (params.filters.length) {
      return this.mediaObjectRS.getMediaObjectCollection(params)
        .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
          return resultListAM.records.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM));
        }));
    }

    return of<MediaObjectViewModel[]>([]);
  }

  public getProjectsCollection(postAMs: PostApiModel[]): Observable<ProjectViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = postAMs
      .map((e: PostApiModel) => this.extractSlug(e.project))
      .filter((slug: string | null): slug is string => !!slug)
      .map((slug) => new FilterParameterApiModel('slug', slug));
    if (params.filters.length) {
      return this.projectRS.getCollection(params)
        .pipe(mergeMap((resultListAM: ResultListApiModel<ProjectApiModel>) => {
          return forkJoin([
            this.getVehicleCollection(resultListAM.records),
            this.getUserCollection(resultListAM.records)
          ]).pipe(map(([vehicleVMs, userVMs]: [VehicleViewModel[], UserViewModel[]]) => {
            return resultListAM.records.map((projectAM: ProjectApiModel) => {
              const projectVM = new ProjectViewModel(projectAM);
              projectVM.vehicle = vehicleVMs.find((vehicleVM: VehicleViewModel) => projectAM.vehicle === vehicleVM['@id']);
              projectVM.user = userVMs.find((userVM: UserViewModel) => projectAM.user === userVM['@id']);
              return projectVM;
            });
          }));
        }));
    }

    return of<ProjectViewModel[]>([]);
  }

  public getVehicleCollection(projectAMs: ProjectApiModel[]): Observable<VehicleViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = projectAMs.map(e => new FilterParameterApiModel('id', e.vehicle));
    return this.vehicleRS.getVehicleCollection(params)
      .pipe(map((resultListAM: ResultListApiModel<VehicleApiModel>) => {
        return resultListAM.records.map((vehicleAM: VehicleApiModel) => {
          return new VehicleViewModel(vehicleAM);
        });
      }));
  }

  public getUserCollection(projectAMs: ProjectApiModel[]): Observable<UserViewModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = projectAMs.map(e => new FilterParameterApiModel('slug', (e.user as string).split('/').pop()));
    return this.userRS.getCollection(params)
      .pipe(map((resultListAM: ResultListApiModel<UserApiModel>) => {
        return resultListAM.records.map((userAM: UserApiModel) => {
          return new UserViewModel(userAM);
        });
      }));
  }

  public getEventCollection(postAMs: PostApiModel[]): Observable<EventApiModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = postAMs
      .map((e: PostApiModel) => this.extractSlug(e.event))
      .filter((slug: string | null): slug is string => !!slug)
      .map((slug) => new FilterParameterApiModel('slug', slug));

    if (params.filters.length) {
      return this.eventRS.getCollection(params).pipe(map((resultListAM: ResultListApiModel<EventApiModel>) => {
        return resultListAM.records;
      }));
    }

    return of<EventApiModel[]>([]);
  }

  private extractSlug(resource: string | { slug?: string } | null | undefined): string | null {
    if (!resource) {
      return null;
    }

    if (typeof resource === 'string') {
      return resource.split('/').pop() || null;
    }

    return resource.slug || null;
  }

  public createComment(content: string, post: PostViewModel, thread?: CommentApiModel, media: MediaObjectInterface[] = []): Observable<CommentApiModel> {
    return this.commentRS
      .create({content, media: media.map(item => item['@id']), type: 'POST_COMMENT', targetObject: post['@id'], thread: thread ? thread['@id'] : null});
  }

  public updateComment(commentPartialAM: { '@id': string, content: string, media?: string[] }): Observable<CommentApiModel> {
    return this.commentRS.put(commentPartialAM).pipe(map((commentAM: CommentApiModel) => {
      return commentAM;
    }));
  }

  public removeComment(commentAM: CommentApiModel): Observable<any> {
    return this.commentRS.delete(commentAM);
  }

  public getCommentCollection(post: PostViewModel, fetchDeleted = false): Observable<any> {
    const params = new QueryParamsApiModel();
    params.filters.push(new FilterParameterApiModel('limit', 100, 'single'));
    params.filters.push(new FilterParameterApiModel('post', post['@id'], 'single'));
    params.filters.push(new FilterParameterApiModel('discriminator', 'POST_COMMENT', 'array'));

    if (fetchDeleted) {
      params.filters.push(new FilterParameterApiModel('soft_deleted', 1, 'single'));
    }

    return this.commentRS.getCollection(params);
  }

  public postMediaObject(file): Observable<MediaObjectApiModel> {
    return this.mediaObjectRS.uploadImage(file);
  }

  public deleteMediaObject(mediaObjectAM: MediaObjectApiModel): Observable<any> {
    return this.mediaObjectRS.deleteMediaObject(mediaObjectAM['@id']);
  }

  public createReaction(reactedWith: string, subject: ReactionSubjectInterface, type: ReactionTypeEnum): Observable<ReactionApiModel> {
    return this.reactionRS
      .create(plainToClass(ReactionApiModel, { reactedWith, subject: subject['@id'], type }));
  }

  public removeReaction(reactionAM: ReactionApiModel): Observable<any> {
    return this.reactionRS.delete(reactionAM);
  }
}
